import { Fragment, PropsWithChildren, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Dialog, DialogTitle, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  useMaxWidth?: boolean;
  className?: string;
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  useMaxWidth = true,
  className,
}: PropsWithChildren<ModalProps>) => {
  const xRef = useRef(null);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={xRef}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </TransitionChild>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-8 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className={twMerge(
                  'transform overflow-hidden rounded-sm bg-white p-6 text-left align-middle shadow-xl transition-all',
                  useMaxWidth && 'w-full',
                )}
              >
                <div className="flex flex-row items-center justify-between bg-gray-100 border-b border-gray-200 p-4 -m-6 mb-4">
                  <div>
                    {title && (
                      <DialogTitle as="h3" className="text-md font-bold uppercase text-sage-700">
                        {title}
                      </DialogTitle>
                    )}
                  </div>
                  <FontAwesomeIcon icon={faXmark} className="w-5 h-5 text-sage-600 cursor-pointer" onClick={onClose} />
                </div>
                <div className={twMerge('mt-2', className)}>{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
